import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { FaHeart, FaUser, FaSearch, FaBars, FaTimes, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import { useFavorites } from "./FavoritesContext";
import { useLanguage } from "./LanguageContext";
import RegisterUser from "./registeruser";
import { FaGoogle } from 'react-icons/fa';
import axios from "axios";
import { api } from "../lib/http";

const API_BASE_URL = "https://shopaholics.ge/api";

/* =================== THEME =================== */
const COLORS = {
  bg: "rgba(255,255,255,0.7)",
  blur: "blur(12px)",
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,0.08)",
  black: "#111",
  brand: "#000",
  accent: "#111",
};

const BP = {
  md: "1200px",
  sm: "1000px",
  xs: "600px",
};

/* =================== WRAPPERS =================== */
const HeaderWrap = styled.header`
  --h: 64px;
  position: sticky;
  top: 0;
  z-index: 2000;
  backdrop-filter: ${COLORS.blur};
  background: ${COLORS.bg};
  border-bottom: 1px solid ${COLORS.line};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 14px;
  align-items: center;

  @media (max-width: ${BP.md}) {
    grid-template-columns: 160px 1fr auto;
  }
  @media (max-width: ${BP.sm}) {
    grid-template-columns: 1fr auto;
  }
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  img {
    height: 60px;
    width: auto;
    display: block;
    margin-top: 10px;
  }
`;

/* =================== SEARCH =================== */
const SearchBox = styled.div`
  position: relative;
  width: 90%;
  @media (max-width: ${BP.sm}) { display: none; }
  z-index: 2999;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 46px;
  border-radius: 12px;
  border: 1px solid ${COLORS.line};
  padding: 0 14px 0 40px;
  font-size: 15px;
  outline: none;
  color: ${COLORS.text};
  background: #fff url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z' stroke='%236b7280' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") 12px center no-repeat;
`;

const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0; right: 0;
  background: #fff;
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  box-shadow: 0 18px 36px rgba(0,0,0,.12);
  max-height: 420px;
  overflow-y: auto;
  z-index: 3000;
  pointer-events: auto;
`;

const ResultRow = styled(Link)`
  display: grid;
  grid-template-columns: 48px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  text-decoration: none;
  color: ${COLORS.text};

  &:hover { background: #fafafa; }

  img {
    width: 48px; height: 48px; border-radius: 10px; object-fit: cover;
    border: 1px solid #f0f0f0;
    background: #fff;
  }
  .name { font-weight: 600; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .brand { font-size: 12px; color: ${COLORS.dim}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .price { font-weight: 700; font-size: 13px; color: ${COLORS.text}; }
`;

const ViewAll = styled(Link)`
  display: block;
  padding: 12px 14px;
  text-align: center;
  text-decoration: none; color: ${COLORS.text};
  font-weight: 700;
  border-top: 1px solid #f2f2f2;
  &:hover{ background:#fafafa; }
`;

const Empty = styled.div`
  padding: 14px;
  color: ${COLORS.dim};
  font-size: 14px;
`;

/* =================== ICONS / ACTIONS =================== */
const Actions = styled.div`
  display: inline-flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;

`;

const LangBtn = styled.button`
  height: 36px; padding: 0 12px;
  border-radius: 10px; border: 1px solid ${COLORS.line};
  background: #fff; font-weight: 700; cursor: pointer;
`;

const IconBtn = styled.button`
  position: relative;
  width: 40px; height: 40px;
  border-radius: 10px;
  border: 1px solid ${COLORS.line};
  background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: transform .15s ease, box-shadow .15s ease;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }
  svg { font-size: 18px; color: ${COLORS.text}; }
`;

const CartBadge = styled.span`
  position: absolute; top: -6px; right: -6px;
  background: #111; color: #fff; border-radius: 999px;
  padding: 2px 6px; font-size: 11px; font-weight: 800;
`;

const FavLink = styled(Link)`
  ${IconBtn};
  color: ${({ $active }) => ($active ? "red" : COLORS.dim)};
`;

/* =================== NAVBAR =================== */
const NavBar = styled.nav`
  border-top: 1px solid ${COLORS.line};
  border-bottom: 1px solid ${COLORS.line};
  background: rgba(255,255,255,0.6);
  backdrop-filter: ${COLORS.blur};
  @media (max-width: ${BP.sm}) { display: none; }
`;

const NavInner = styled.div`
  max-width: 1280px; margin: 0 auto; padding: 0 16px;
  display: flex; gap: 18px; align-items: center; height: 52px;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${COLORS.text};
  font-weight: 700;
  font-size: 14px;
  padding: 6px 8px; border-radius: 8px;
  &:hover { background: #f7f7f7; }
`;

const DropWrap = styled.div`
  position: relative;
  &:hover > div { opacity: 1; visibility: visible; transform: translateY(6px); }
`;
const DropBtn = styled.button`
  background: transparent; border: 0; font-weight: 700; color: ${COLORS.text}; cursor: pointer;
  padding: 6px 8px; border-radius: 8px;
  &:hover { background: #f7f7f7; }
`;
const DropMenu = styled.div`
  position: absolute; top: 100%; left: 0;
  background: #fff; border: 1px solid ${COLORS.line};
  border-radius: 12px; box-shadow: 0 18px 36px rgba(0,0,0,.12);
  padding: 14px; min-width: 280px; display: grid; gap: 8px;
  opacity: 0; visibility: hidden; transform: translateY(0); transition: .2s;
  a { text-decoration: none; color: ${COLORS.text}; padding: 8px 10px; border-radius: 8px; }
  a:hover { background: #f7f7f7; }
`;

/* =================== MOBILE BAR =================== */
  const MobileBar = styled.div`
  display: none;
  @media (max-width: ${BP.sm}) {
    display: grid;
    display: grid; grid-template-columns: 48px minmax(0,1fr);
    grid-template-rows: auto auto; /* ⬅️ 2 რიგი: 1) ბურგერი+ძებნა 2) აიქონები */
    gap: 10px;
    padding: 10px 16px 14px;
    border-top: 1px solid ${COLORS.line};
    background: rgba(255,255,255,0.8);
    backdrop-filter: ${COLORS.blur};
  }
`;
  const ContactDropdownMobile = styled.div`
    display: inline-flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;

    @media (max-width: ${BP.sm}) {
    display: none;
}
`;


const MobileIconRow = styled.div`
  grid-column: 1 / -1;       /* ორივე სვეტი გადაიფაროს */
  display: flex;
  align-items: center;
  justify-content: space-between; /* სამივე აიქონი გადანაწილდეს */
  gap: 10px;
  margin-top: 2px;
  z-index: 1;
`;

const MobileSearch = styled.div`
  @media (max-width: ${BP.sm}) {
    position: relative;
    input{
      width: 100%; max-width: 100%; height: 40px;
      box-sizing: border-box;
      border-radius: 12px; border: 1px solid ${COLORS.line};
      padding: 0 12px 0 40px; background: #fff;
      background-image: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21 21l-4.35-4.35m1.85-4.65a7 7 0 11-14 0 7 7 0 0114 0z' stroke='%236b7280' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: 12px center;
      outline: none; font-size: 16px; /* iOS zoom-ის პრევენცია */
    }
  }
`;
const Burger = styled(IconBtn)``;

/* =================== DRAWER =================== */
const DrawerOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 2100;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transition: .2s;
`;
const Drawer = styled.aside`
  position: fixed; top: 0; left: 0; height: 100vh; width: 86vw; max-width: 360px;
  background: #fff; border-right: 1px solid ${COLORS.line};
  transform: translateX(${({ $open }) => ($open ? "0" : "-100%")});
  transition: transform .25s ease;
  z-index: 2200; display: grid; grid-template-rows: auto 1fr auto;
`;
const DrawerTop = styled.div`
  display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid ${COLORS.line};
`;
const DrawerList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px 8px;
  display: grid;
  gap: 20px;

  /* 👉 scroll */
  max-height: calc(100vh - 120px); 
  overflow-y: auto;

  li a, li button {
    width: 100%;
    text-align: left;
    padding: 10px 12px;
    border-radius: 10px;
    border: 0;
    background: transparent;
    font-weight: 700;
    color: ${COLORS.text};
  }

  li a:hover, li button:hover {
    background: #f7f7f7;
  }

  &::-webkit-scrollbar {
  width: 6px;
}
&::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,.2);
  border-radius: 6px;
}

`;

const DrawerFooter = styled.div`
  padding: 14px 12px; border-top: 1px solid ${COLORS.line}; display: grid; gap: 8px;
`;

/* =================== SMALL HELPERS =================== */
const ContactDropdown = styled.div`
  position: relative;
  display: inline-block;
  &:hover > div { opacity: 1; visibility: visible; transform: translateY(6px); }
  z-index: 2999;
`;
const ContactBtn = styled(IconBtn)`
  width: auto; padding: 0 12px; gap: 8px; border-radius: 999px;
  display: inline-flex; align-items: center; svg{ font-size: 16px;}
`;
const ContactMenu = styled.div`
  position: absolute; top: calc(100% + 6px); right: 0; background:#fff; border:1px solid ${COLORS.line};
  border-radius: 12px; box-shadow: 0 18px 36px rgba(0,0,0,.12);
  padding: 10px; display: grid; gap: 6px; min-width: 220px;
  opacity:0; visibility:hidden; transform:translateY(0); transition:.2s;
  a{ display:flex; gap:8px; align-items:center; padding:8px 10px; border-radius:8px; color:${COLORS.text}; text-decoration:none; }
  a:hover{ background:#f7f7f7;}
`;
// დაამატე styled-ების სექციაში:

const SearchResultsMobile = styled.div`
  position: fixed;
  left: ${({ $left }) => `${$left}px`};
  top:  ${({ $top })  => `${$top}px`};
  width: ${({ $width }) => `${$width}px`};
  top: ${({ $top }) => `${$top}px`};   /* JS-ით მოვაწვდით მითითებულ პოზიციას */
  background: #fff;
  border: 1px solid ${COLORS.line};
  border-radius: 14px;
  box-shadow: 0 18px 36px rgba(0,0,0,.12);
 -webkit-overflow-scrolling: touch;
 overscroll-behavior: contain;  /* ქვემოთ გვერდი აღარ „იღლიბება“ */
  overflow-y: auto;
  z-index: 5000;
  padding: 6px 0;                /* input-ს ქვემოთ ოდნავ ჰაერი */
`;

// ---- Image pick helpers ----
const pickFirst = (arr, path) => {
  if (!Array.isArray(arr)) return null;
  for (const item of arr) {
    const imgs = Array.isArray(item?.images) ? item.images : [];
    if (imgs.length && imgs[0]) return imgs[0];
  }
  return null;
};

const absolutize = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  // თუ ბექი აბრუნებს.Relative ბილიკებს, აქ ვაქცევთ აბსოლუტურად
  return `https://shopaholics.ge/${String(url).replace(/^\/+/, "")}`;
};

/** იპოვოს სურათი ნებისმიერი დონის მიხედვით:
 * 1) product.imageUrls[0]
 * 2) milliliters[].images[0]
 * 3) accessory_variants[].images[0]
 * 4) product.image_url
 * 5) placeholder
 */
const getPrimaryImage = (p) => {
  const fromProduct =
    (Array.isArray(p?.imageUrls) && p.imageUrls.length && p.imageUrls[0]) || null;

  const fromML = pickFirst(p?.milliliters);
  const fromVar = pickFirst(p?.accessory_variants);

  const candidate =
    fromProduct ||
    fromML ||
    fromVar ||
    p?.image_url ||
    "/placeholder.jpg";

  return absolutize(candidate) || "/placeholder.jpg";
};

/* =================== COMPONENT =================== */
export default function Header({ cart = [], toggleCartOverlay }) {
  const navigate = useNavigate();
  const { hasFavorites } = useFavorites();
  const { language, toggleLanguage, t } = useLanguage();
  const [user, setUser] = useState(null);

  /* Search state */
  const [allProducts, setAllProducts] = useState([]);
  const [q, setQ] = useState("");
  const [openResults, setOpenResults] = useState(false);
  const [results, setResults] = useState([]);
 const searchBoxRef = useRef(null);      // დესკტოპის ძებნა
 const mobileSearchRef = useRef(null);
 const mobileDropRef   = useRef(null);
 const [mobileDropRect, setMobileDropRect] = useState({ top: 0, left: 0, width: 0 });

  /* Modal state */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  /* Drawer */
  const [drawerOpen, setDrawerOpen] = useState(false);

  /* Cart count */
  const totalItems = useMemo(() => cart.reduce((s, it) => s + (it.quantity || 0), 0), [cart]);

  /* Fetch products once */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`${API_BASE_URL}/get_products.php`);
        setAllProducts(Array.isArray(res.data) ? res.data : []);
      } catch {
        setAllProducts([]);
      }
    })();
  }, []);

  /* Filter results debounced */
  useEffect(() => {
    if (!q.trim()) { setResults([]); setOpenResults(false); return; }
    const tmr = setTimeout(() => {
      const k = q.trim().toLowerCase();
      const out = allProducts
        .filter(p => (p.name||"").toLowerCase().includes(k) || (p.brand||"").toLowerCase().includes(k))
        .slice(0, 8);
      setResults(out);
      setOpenResults(true);
    }, 200);
    return () => clearTimeout(tmr);
  }, [q, allProducts]);

  /* Close search dropdown on outside click */
  useEffect(() => {
  const off = (e) => {
    const inDesktop = searchBoxRef.current?.contains(e.target);
    const inMobileI = mobileSearchRef.current?.contains(e.target);
    const inMobileD = mobileDropRef.current?.contains(e.target);
    if (!inDesktop && !inMobileI && !inMobileD) setOpenResults(false);
  };
  document.addEventListener("click", off);   // bubbling, όχι capture
  return () => document.removeEventListener("click", off);
  }, []);

  /* User from localStorage */
  useEffect(() => {
    const syncUser = () => {
      try {
        const raw = localStorage.getItem("user");
        setUser(raw ? JSON.parse(raw) : null);
      } catch { setUser(null); }
    };
    syncUser();
    window.addEventListener("storage", syncUser);
    return () => window.removeEventListener("storage", syncUser);
  }, []);

  const openAuthModal = (register) => { setIsRegistering(register); setIsModalOpen(true); };
  const closeAuthModal = () => setIsModalOpen(false);



// მობილური dropdown პოზიციონირება — განახლდეს resize/scroll/focus-ზე
useEffect(() => {
  const update = () => {
    if (!mobileSearchRef.current) return;
    const r = mobileSearchRef.current.getBoundingClientRect();
    setMobileDropRect({ top: r.bottom + 8, left: r.left, width: r.width });
  };
  update();
  window.addEventListener("resize", update);
  window.addEventListener("scroll", update, true);
  return () => {
    window.removeEventListener("resize", update);
    window.removeEventListener("scroll", update, true);
  };
}, []);


  return (
    <>
      <HeaderWrap>
        <Container>
          {/* Brand */}
          <Brand to="/"><img src="/images/logo.png" alt="Shopaholics" /></Brand>

          {/* Search (desktop) */}
          <SearchBox ref={searchBoxRef}>
            <SearchInput
              placeholder={t("header.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onFocus={() => { if (results.length) setOpenResults(true); }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && q.trim()) {
                  navigate(`/products?search=${encodeURIComponent(q.trim())}`);
                  setOpenResults(false);
                }
              }}
            />
            {openResults && (
              <SearchResults>
                {results.length ? (
                  <>
                    {results.map((p, i) => {
                      const img = getPrimaryImage(p);
                      const priceList = Array.isArray(p?.milliliters) ? p.milliliters.map(m => parseFloat(m.sale_price||m.price||0)).filter(x=>x>0).sort((a,b)=>a-b) : [];
                      const priceText = priceList.length ? `${priceList[0]} ${t("newproducts.currency")}` : "";
                      return (
                        <ResultRow to={`/product/${p.product_id}`} key={p.product_id || `${p.name}-${i}`} onClick={() => setOpenResults(false)}>
                          <img src={img} alt={p.name} />
                          <div>
                            <div className="name">{p.name}</div>
                            <div className="brand">{p.brand}</div>
                          </div>
                          <div className="price">{priceText}</div>
                        </ResultRow>
                      );
                    })}
                    <ViewAll to={`/products?search=${encodeURIComponent(q.trim())}`} onClick={() => setOpenResults(false)}>
                      {language === "ka" ? "ყველა შედეგის ნახვა" : "See all results"}
                    </ViewAll>
                  </>
                ) : (
                  <Empty>{language === "ka" ? "შედეგი არ მოიძებნა" : "No results"}</Empty>
                )}
              </SearchResults>
            )}
          </SearchBox>

          {/* Actions */}
          <Actions>
            <LangBtn onClick={toggleLanguage}>
              {language === "en" ? "Georgian" : "English"}
            </LangBtn>

            <ContactDropdown>
              <ContactBtn>
                <FaPhoneAlt /> {t("header.contact")}
              </ContactBtn>
              <ContactMenu>
                <a href="tel:+995558641621"><FaPhoneAlt/> {t("header.callUs")}</a>
                <a href="mailto:support@shopaholics.ge"><FaEnvelope/> {t("header.messageUs")}</a>
              </ContactMenu>
            </ContactDropdown>
            <ContactDropdownMobile>
            <IconBtn onClick={toggleCartOverlay} aria-label="Cart">
              <i className="fas fa-shopping-basket" />
              {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
            </IconBtn>

            <FavLink to="/favorites" $active={hasFavorites ? 1 : 0} aria-label="Favorites">
              <FaHeart />
            </FavLink>

            {/* User */}
            {user ? (
              <IconBtn onClick={() => openAuthModal(false)} aria-label="My account">
                <FaUser />
              </IconBtn>
            ) : (
              <IconBtn onClick={() => openAuthModal(false)} aria-label="Sign in">
                <FaUser />
              </IconBtn>
            )}
            </ContactDropdownMobile>

            {/* Burger (mobile) */}
            <Burger onClick={() => setDrawerOpen(true)} aria-label="Menu" style={{ display: "none" }}>
              <FaBars />
            </Burger>
          </Actions>
        </Container>

        {/* Desktop Nav */}
        <NavBar>
          <NavInner>
            <NavLink to="/products">{t("menu.shopnow")}</NavLink>

            <DropWrap>
              <DropBtn>{t("menu.shop")} ▾</DropBtn>
              <DropMenu>
                <Link to="/products?category=perfume">{t("menu.perfume")}</Link>
                <Link to="/products?category=skin-care">{t("menu.skincare")}</Link>
                <Link to="/products?category=hair-and-body-care">{t("menu.hairandbody")}</Link>
              </DropMenu>
            </DropWrap>

            <NavLink to="/products?category=atomizers">{t("menu.atomizers")}</NavLink>
            <NavLink to="/products?category=accessories">{t("menu.accessories")}</NavLink>
            <NavLink to="/gift-sets">{t("menu.giftsets")}</NavLink>
            <NavLink to="/BrandsPage">{t("menu.brands")}</NavLink>
            <NavLink to="/about-us">{t("menu.aboutus")}</NavLink>
          </NavInner>
        </NavBar>

        {/* Mobile compact bar (with inline search + burger) */}
        <MobileBar>
          <Burger onClick={() => setDrawerOpen(true)} aria-label="Menu">
            <FaBars />
          </Burger>

          <MobileSearch>
            <input
             ref={mobileSearchRef}
              placeholder={t("header.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && q.trim()) {
                  navigate(`/products?search=${encodeURIComponent(q.trim())}`);
                }
              }}
              onFocus={() => { if (results.length) setOpenResults(true); }}
            />
   {/* მობილურის dropdown შედეგები */}
 {openResults && createPortal(
   <SearchResultsMobile
     ref={mobileDropRef}
     $top={mobileDropRect.top}
     $left={mobileDropRect.left}
     $width={mobileDropRect.width}
     style={{ maxHeight: `calc(100vh - ${Math.max(16, mobileDropRect.top)}px)` }}
   >
     {/* იგივე შიგთავსი, რაც Desktop ResultRow/ViewAll-ში გაქვს */}
     {results.length ? (
       <>
         {results.map((p, i) => {
            const img = getPrimaryImage(p);
           const priceList = Array.isArray(p?.milliliters)
             ? p.milliliters.map(m => parseFloat(m.sale_price||m.price||0)).filter(x=>x>0).sort((a,b)=>a-b)
             : [];
           const priceText = priceList.length ? `${priceList[0]} ${t("newproducts.currency")}` : "";
           return (
             <ResultRow to={`/product/${p.product_id}`} key={p.product_id || `${p.name}-${i}`} onClick={() => setTimeout(() => setOpenResults(false), 0)}>
               <img src={img} alt={p.name} />
               <div>
                 <div className="name">{p.name}</div>
                 <div className="brand">{p.brand}</div>
               </div>
               <div className="price">{priceText}</div>
             </ResultRow>
           );
         })}
         <ViewAll to={`/products?search=${encodeURIComponent(q.trim())}`} onClick={() => setOpenResults(false)}>
           {language === "ka" ? "ყველა შედეგის ნახვა" : "See all results"}
         </ViewAll>
       </>
     ) : (
       <Empty>{language === "ka" ? "შედეგი არ მოიძებნა" : "No results"}</Empty>
     )}
   </SearchResultsMobile>,
   document.body
 )}
  </MobileSearch>


        {/* ⬇️ ახალი ქვედა რიგი — ფავორიტი • კალათა • იუზერი */}
        <MobileIconRow>
          <IconBtn as={Link} to="/favorites" aria-label="Favorites" title={t("menu.favorites")}>
            <FaHeart style={{ color: hasFavorites ? "red" : COLORS.dim }} />
          </IconBtn>

          <IconBtn onClick={toggleCartOverlay} aria-label="Cart" title={t("menu.cart")}>
            <i className="fas fa-shopping-basket" />
            {totalItems > 0 && <CartBadge>{totalItems}</CartBadge>}
          </IconBtn>

            <IconBtn onClick={() => openAuthModal(false)} aria-label="My account" title={t("header.myaccount")}>
              <FaUser />
            </IconBtn>
        </MobileIconRow>
        </MobileBar>

      </HeaderWrap>

      {/* Drawer */}
      <DrawerOverlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} />
      <Drawer $open={drawerOpen} role="dialog" aria-modal="true">
        <DrawerTop>
          <Brand to="/" onClick={() => setDrawerOpen(false)}>
            <img src="/images/logo.png" alt="Shopaholics" />
          </Brand>
          <IconBtn onClick={() => setDrawerOpen(false)} aria-label="Close"><FaTimes/></IconBtn>
        </DrawerTop>

        <DrawerList>
          <li><Link to="/products" onClick={() => setDrawerOpen(false)}>{t("menu.shopnow")}</Link></li>
          <li><Link to="/products?category=perfume" onClick={() => setDrawerOpen(false)}>{t("menu.perfume")}</Link></li>
          <li><Link to="/products?category=skin-care" onClick={() => setDrawerOpen(false)}>{t("menu.skincare")}</Link></li>
          <li><Link to="/products?category=hair-and-body-care" onClick={() => setDrawerOpen(false)}>{t("menu.hairandbody")}</Link></li>
          <li><Link to="/products?category=atomizers" onClick={() => setDrawerOpen(false)}>{t("menu.atomizers")}</Link></li>
          <li><Link to="/products?category=accessories" onClick={() => setDrawerOpen(false)}>{t("menu.accessories")}</Link></li>
          <li><Link to="/gift-sets" onClick={() => setDrawerOpen(false)}>{t("menu.giftsets")}</Link></li>
          <li><Link to="/BrandsPage" onClick={() => setDrawerOpen(false)}>{t("menu.brands")}</Link></li>
          <li><Link to="/about-us" onClick={() => setDrawerOpen(false)}>{t("menu.aboutus")}</Link></li>
        </DrawerList>

        <DrawerFooter>
          <LangBtn onClick={toggleLanguage}>
            {language === "en" ? "Georgian" : "English"}
          </LangBtn>
          {user ? (
            <Link onClick={() => { setDrawerOpen(false); openAuthModal(false); }} style={{ textDecoration:"none" }}>
              <IconBtn style={{ width:"100%" }}><FaUser/>&nbsp;{t("header.myaccount")}</IconBtn>
            </Link>
          ) : (
            <>
              <IconBtn style={{ width:"100%" }} onClick={() => { setDrawerOpen(false); openAuthModal(false); }}>
                {t("login.signin")}
              </IconBtn>
              <IconBtn style={{ width:"100%" }} onClick={() => { setDrawerOpen(false); openAuthModal(true); }}>
                {t("login.signup")}
              </IconBtn>
              <IconBtn style={{ width:"100%" }} onClick={() => (window.location.href = "/api/google_callback.php")}>
              <FaGoogle className="social-icon" />&nbsp;{t("login.google")}
              </IconBtn>
            </>
          )}
        </DrawerFooter>
      </Drawer>

      {/* Auth Modal */}
      {isModalOpen && (
        <RegisterUser
          isRegistering={isRegistering}
          toggleModal={closeAuthModal}
          isModalOpen={isModalOpen}
          setUser={setUser}
        />
      )}
    </>
  );
}
